import { useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import useAuth from '../hooks/useAuth';
import useIdleTimeout, { formatCountdown } from '../hooks/useIdleTimeout';
import RestService from '../services/RestService';
import TokenService from '../services/TokenService';
import {
  DEFAULT_IDLE_TIMEOUT_SECONDS,
  IDLE_WARNING_MS,
  SESSION_ACTIVITY_STORAGE_KEY,
  setIdleLogoutReason,
} from '../utils/sessionIdle';

/**
 * Signs the user out after the session idle timeout (30 minutes by default,
 * from user-management), with a five-minute warning first. While the user is
 * active it pings user-management, whose own idle timeout ignores background
 * requests.
 */
export default function IdleSessionGuard() {
  const { isAuthenticated, logout } = useAuth();
  // 0 = this account is exempt from the idle timeout; missing = platform default.
  const timeoutSeconds =
    TokenService.getLocalTokenData()?.idleTimeoutSeconds ?? DEFAULT_IDLE_TIMEOUT_SECONDS;
  const exempt = timeoutSeconds <= 0;

  const onIdle = useCallback(() => {
    setIdleLogoutReason();
    void logout();
  }, [logout]);

  const onPing = useCallback(() => {
    const refresh = TokenService.getLocalRefreshToken();
    return refresh ? RestService.sessionActivity(refresh) : undefined;
  }, []);

  const { secondsLeft, stayActive } = useIdleTimeout({
    enabled: isAuthenticated && !exempt,
    timeoutMs: timeoutSeconds * 1000,
    warningMs: IDLE_WARNING_MS,
    storageKey: SESSION_ACTIVITY_STORAGE_KEY,
    onIdle,
    onPing,
  });

  return (
    <Dialog
      open={isAuthenticated && !exempt && secondsLeft !== null}
      onClose={stayActive}
      maxWidth="xs"
    >
      <DialogTitle>Are you still there?</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You haven&apos;t been active for a while. For your security you&apos;ll be signed out in{' '}
          <strong>{formatCountdown(secondsLeft ?? 0)}</strong> ({Math.round(timeoutSeconds / 60)}{' '}
          minutes of inactivity).
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={onIdle}>
          Sign out
        </Button>
        <Button variant="contained" onClick={stayActive} autoFocus>
          Stay signed in
        </Button>
      </DialogActions>
    </Dialog>
  );
}
